/**
 * הרשאות לחברות — granting a person selected companies. D5 / CG-1..CG-7, OA-0001 §3.7 / §3.8.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * THIS IS THE SECURITY SCREEN. THE ROLES SCREEN IS NOT.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Three tables, three capabilities, three screens, and only the middle one is access control:
 *
 *   role      → what a user may DO                  `core.role_permission`
 *   grant     → which companies they may do it ON   `core.user_company_access`   ← THIS SCREEN
 *   portfolio → whose dashboard it appears on       `core.company_portfolio_assignment`
 *
 * The third one is the trap, and OA-0001 §3.8 rules on it: PORTFOLIO IS NOT SECURITY. Assigning a
 * company to one partner changes what they SEE FIRST and cannot change what anybody may OPEN — the
 * portfolio table has no reader in the authorization path at all, and a guardrail names it so it
 * cannot acquire one. Legacy's counter-example is a hardcoded four-name staff list that AUTO-GRANTED
 * companies, with the names already drifted from the live store.
 *
 * ── WHAT THE RULES MAKE THIS SCREEN LOOK LIKE ───────────────────────────────────────────────────
 *
 *   CG-2  There is no `deny` level, so there is no "block" control here. EXCLUSION IS THE ABSENCE OF
 *         A ROW. To exclude a company from a person, do not grant it — including from the owner of
 *         the office, and including for conflict of interest.
 *   CG-4  Choosing five companies writes FIVE INDIVIDUALLY REVOCABLE ROWS, never one bulk flag. The
 *         list below is a set of checkboxes for exactly that reason.
 *   CG-6  Revoking one removes one (person, company) pair and nothing else.
 *   CG-7  A revocation carries a NAMED reason code, because "conflict of interest" is a recorded
 *         classification with a column, not a note in an audit row.
 *   D-12  Legacy's console wrote `assigned_users` and NO AUTHORIZATION PATH READ IT: the screen
 *         reported success and access did not change. The table behind this screen has exactly one
 *         reader — the gate — and a guardrail asserts that reader exists.
 *
 * ── AND WHY THERE IS NO DROPDOWN ────────────────────────────────────────────────────────────────
 *
 * 09 §3.4: company and period are chosen in the context bar and nowhere else, and
 * `shell/contextSingleSource.test.ts` scans the source tree to keep it that way. The companies here
 * are not a CONTEXT choice — they are the subject of an action — so they are checkboxes on a form,
 * which is also the shape CG-4 wants.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { TextKey } from '../i18n/catalogue';
import { useLocale } from '../i18n/LocaleProvider';
import type { FormEvent } from 'react';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { DataTable } from '../components/DataTable';
import { InfoNote } from '../components/InfoNote';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { StateBlock } from '../components/StateBlock';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { Column } from '../components/DataTable';
import type { AccessLevel, CompanyGrantSummary } from '../api/contract';
import { accessLevelHe, formatInstantHe } from '../api/adminVocabulary';
import { OFFERED_ACCESS_LEVELS } from '../api/contract';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { ServerRead } from '../components/ServerRead';
import { bothOf, listOf, useServerData } from '../api/useServerData';
import { routes } from '../routes';


/* The hint is a KEY, resolved where it is rendered: a module-level constant would hold whatever
   language was active at import and never follow a change. */
const LEVELS: readonly { readonly code: AccessLevel; readonly hint: TextKey }[] = [
  { code: 'read', hint: 'access.readBody' },
  { code: 'write', hint: 'access.writeBody' },
  // `approve` is NOT offered. See `OFFERED_ACCESS_LEVELS` below.
  { code: 'approve', hint: 'access.approveBody' },
];

type Outcome =
  | { readonly kind: 'granted'; readonly rows: number }
  | { readonly kind: 'revoked'; readonly companyUid: string }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

export function TenantCompanyAccessScreen(): JSX.Element {
  const { t } = useLocale();
  const ctx = useWorkContext();
  const [membershipUid, setMembershipUid] = useState('');
  const [chosenCompanyUids, setChosenCompanyUids] = useState<readonly string[]>([]);
  const [level, setLevel] = useState<AccessLevel>('read');
  const [pending, setPending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  /*
   * BOTH AXES, READ SEPARATELY. **Phase 2 §6, vertical A.**
   *
   * `tenant.member.read` gives the people to grant to; `tenant.grant.read` gives what is granted
   * already. Both are required to render this screen honestly — a grant table with no rows and a
   * grant table that could not be read are opposite statements about whether a colleague can open a
   * client's file, and 01 §A5's whole point is that the ABSENCE of a grant IS the exclusion.
   *
   * The COMPANIES come from the session context rather than a third read: `ctx.visibleCompanies` is
   * already the set this session may see, and re-reading it here would be a second answer to a
   * question the shell has asked once.
   */
  const readMembers = useCallback(() => callOperation('tenant.member.read', {}), []);
  const readGrants = useCallback(() => callOperation('tenant.grant.read', {}), []);
  /*
   * ── THE COMPANIES TO GRANT COME FROM THE REGISTER. **Section 1, 2026-10-25.** ────────────────
   *
   * They used to come from `ctx.visibleCompanies`, and the comment above still describes why that
   * seemed right: one source, already read by the shell. It was wrong for THIS screen, and the
   * deadlock is the proof -- `visibleCompanies` is grant-filtered, so the set offered as "companies
   * you may grant access to" excluded every company nobody had been granted yet. A company created
   * a minute ago could never be granted to anyone, including the person who created it.
   *
   * The set a grant screen must offer is the office's companies, which is what the register is.
   */
  const readRegister = useCallback(() => callOperation('companies.register.read', {}), []);
  const membersRead = useServerData(readMembers);
  const grantsRead = useServerData(readGrants);
  const registerRead = useServerData(readRegister);

  const members = listOf(membersRead.result, 'members');
  const grants = listOf(grantsRead.result, 'grants');
  const companies = listOf(registerRead.result, 'companies');

  /*
   * ── THE SCREEN OPENS ON THE PERSON WHO IS SIGNED IN. **Section 2, 2026-10-26.** ─────────────
   *
   * *"יש להציג את המשתמש המקים כברירת מחדל ... אין להעניק אוטומטית הרשאות גורפות"* -- both halves.
   * They created the company a moment ago and are the overwhelmingly likely subject, so the radio
   * starts on them instead of on nothing.
   *
   * **IT GRANTS NOTHING.** A preselected radio is a default on a FORM: the companies, the level
   * and the button are all still required, and the gate still demands a step-up for the capability.
   * The effect is that nobody has to find themselves in a list of their own colleagues.
   *
   * It runs once the members arrive and only while the field is untouched, so it can never
   * overwrite a choice. `userUid` is what makes the match possible and the browser contract did
   * not declare it until this round.
   */
  const myMembershipUid = useMemo(
    () => members.find((m) => m.userUid === ctx.session?.userUid)?.membershipUid ?? '',
    [members, ctx.session],
  );
  useEffect(() => {
    if (membershipUid === '' && myMembershipUid !== '') setMembershipUid(myMembershipUid);
  }, [membershipUid, myMembershipUid]);

  const companyNames = useMemo(
    () => new Map(companies.map((c) => [c.companyUid, c.legalName])),
    [companies],
  );

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={t('access.title')} requires="TENANT" actual={ctx.surface} />;
  }

  const rowsForMember = membershipUid === '' ? grants : grants.filter((g) => g.membershipUid === membershipUid);

  function toggleCompanyUid(companyUid: string): void {
    setChosenCompanyUids((current) =>
      current.includes(companyUid)
        ? current.filter((uid) => uid !== companyUid)
        : [...current, companyUid],
    );
  }

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setPending(true);
    setOutcome(null);
    try {
      // CG-4: N companies, N rows. The route takes an array for that reason, not for convenience.
      const result = await callOperation('tenant.grant.create', {
        body: { membershipUid, companyUids: chosenCompanyUids, accessLevel: level },
      });
      setOutcome({ kind: 'granted', rows: result.granted });
      setChosenCompanyUids([]);
      // RE-READ. The table below is the office's record of who can open which client's file, and a
      // locally-patched copy of it is the last thing in this product that should drift.
      grantsRead.reload();
    } catch (err: unknown) {
      // Granting company access requires step-up (04 §2.3). The gate says so through the ordinary
      // denial channel — `stepup_required` has its own Hebrew — and a handler-side step-up carries
      // `need` and is NOT a denial. `classifyActionError` keeps the two apart.
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setPending(false);
    }
  }

  /**
   * ONE ROW. The route takes the pair and nothing wider, so there is no request shape here that could
   * remove a person's access to everything at once — which is what makes the exclusion model
   * reversible without making it coarse.
   */
  async function revoke(grant: CompanyGrantSummary): Promise<void> {
    setPending(true);
    setOutcome(null);
    try {
      await callOperation('tenant.grant.revoke', {
        body: { membershipUid: grant.membershipUid, companyUid: grant.companyUid },
      });
      setOutcome({ kind: 'revoked', companyUid: grant.companyUid });
      grantsRead.reload();
    } catch (err: unknown) {
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setPending(false);
    }
  }

  const grantColumns: readonly Column<CompanyGrantSummary>[] = [
    {
      key: 'company',
      headerHe: t('access.company'),
      render: (g) => companyNames.get(g.companyUid) ?? t('access.companyUnavailable'),
    },
    {
      key: 'person',
      headerHe: t('access.user'),
      render: (g) =>
        members.find((m) => m.membershipUid === g.membershipUid)?.displayName ?? t('access.userUnavailable'),
    },
    { key: 'level', headerHe: t('access.level'), render: (g) => accessLevelHe(g.accessLevel) },
    {
      key: 'granted',
      headerHe: t('access.grantedAt'),
      render: (g) => <span dir="ltr">{formatInstantHe(g.grantedAtUtc) ?? '—'}</span>,
    },
    {
      key: 'expires',
      headerHe: t('common.validUntil'),
      render: (g) => (
        <span dir="ltr">{formatInstantHe(g.expiresAtUtc) ?? t('access.noEnd')}</span>
      ),
    },
    {
      key: 'revoke',
      headerHe: t('common.remove'),
      /*
        CG-6 AS A CONTROL: removing one row changes exactly one (person, company) pair.
        A grant could be CREATED and never removed, which made every mistake permanent and made
        conflict-of-interest exclusion — D5's explicit requirement — a thing an office could enter and
        not leave. There is deliberately no "revoke all" button beside it: bulk provisioning writes N
        INDIVIDUALLY REVOCABLE rows (CG-4), and a control that removed them together would be the
        "all companies" flag the model does not have, arriving through the back door.
      */
      render: (g) => (
        <button
          type="button"
          className="btn btn--quiet"
          disabled={pending}
          onClick={() => void revoke(g)}
        >
          {t('access.removeTitle')}
        </button>
      ),
    },
  ];

  return (
    <AppShell
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated
      titleHe={t('access.title')}
      backToParent={{ labelHe: t('settings.title'), href: routes.tenantAdmin }}
    >
      {/*
        IT WAS A FULL NOTICE BLOCK AND IT IS THE SAME SENTENCE. **2026-10-25.** The rule is worth
        stating -- *"admin sees everything"* is the assumption it contradicts -- and it is not news
        on every visit, so it moves behind the marker and the form it was pushing down comes up.
      */}
      <InfoNote summaryHe={t('access.noBlocklist')}>
        <p>
          {t('access.noBlocklistBody')}
        </p>
      </InfoNote>

      <ServerRead
        result={bothOf(membersRead.result, grantsRead.result)}
        whatHe={t('access.reading')}
      >
        {() => (
          <>
            <form className="form" onSubmit={(e) => void submit(e)}>
              {/*
                THE ACTION, AT THE TOP. **Owner, 2026-10-25:** *"למה ביצוע הרשאה נמצא למטה אבקש
                לפרוס את זה למעלה יותר שיהיה ברור"*.

                The summary beside it is what makes a button at the top of a form readable: it says
                what is currently selected, so the person does not have to scroll back down to find
                out why the button is still disabled.
              */}
              <div className="form__head">
                <h2 className="form__title">{t('access.grantTitle')}</h2>
                <p className="form__head-summary">
                  {membershipUid === ''
                    ? t('access.noUserYet')
                    : chosenCompanyUids.length === 0
                      ? t('access.noCompanyYet')
                      : `${t('access.chosen')} · ${String(chosenCompanyUids.length)} · ${accessLevelHe(level)}`}
                </p>
                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={pending || membershipUid === '' || chosenCompanyUids.length === 0}
                >
                  {pending
                    ? t('common.sending')
                    : chosenCompanyUids.length === 0
                      ? t('access.grant')
                      : `${t('access.grant')} · ${String(chosenCompanyUids.length)}`}
                </button>
              </div>
              {/*
                THE STANDING NOTE IS GONE. **Owner, 2026-10-28:** *"אני לא רוצה לראות הסברים בתוך
                המערכת. המערכת צריכה להיות נקיה."* It said a second identity check MIGHT be asked for and
                that the action is recorded either way — a sentence that is true, standing, on every
                visit, and that nobody can act on. The step-up either happens, in which case the
                prompt itself says so, or it does not. The audit row is written whether or not a
                paragraph announces it.
              */}

              <fieldset className="field field--choice">
                <legend className="field__label">{t('access.user')}</legend>
                {members.map((m) => (
                  <label key={m.membershipUid} className="choice">
                    <input
                      type="radio"
                      name="membership"
                      value={m.membershipUid}
                      checked={membershipUid === m.membershipUid}
                      onChange={() => setMembershipUid(m.membershipUid)}
                    />
                    <span>
                      {m.displayName}
                      <span className="choice__hint" dir="ltr">
                        {m.email}
                      </span>
                    </span>
                  </label>
                ))}
              </fieldset>

              <fieldset className="field field--choice">
                <legend className="field__label">{t('access.companiesToGrant')}</legend>
                {companies.map((c) => (
                  <label key={c.companyUid} className="choice">
                    <input
                      type="checkbox"
                      name="companyUids"
                      value={c.companyUid}
                      checked={chosenCompanyUids.includes(c.companyUid)}
                      onChange={() => toggleCompanyUid(c.companyUid)}
                    />
                    <span>{c.legalName}</span>
                  </label>
                ))}
              </fieldset>

              {/*
                TWO LEVELS, NOT THREE. **Owner, 2026-10-28:** *"ושוב רמת הגישה אני לא מבין מה זה
                צפייה עדכון אישור, דיברנו על זה."* He had said it before, and this screen still offered all
                three while the invitation screen had already been corrected — so the same choice
                looked different in two places, and here two of the three options rendered with the
                SAME LABEL, which is how the screenshot of 2026-10-28 showed it.

                `OFFERED_ACCESS_LEVELS` carries the evidence: in `ACCESS_LEVEL_PERMITS`, the rows for
                `write` and `approve` are IDENTICAL, so the gate cannot tell them apart. A third
                option that grants exactly what the second grants is a question with no answer.

                Nothing was removed from the vocabulary, the CHECK or the gate: a grant already
                recorded as `approve` still reads back as itself through `accessLevelHe`.
              */}
              <label className="field">
                <span className="field__label">{t('access.level')}</span>
                <select
                  className="field__control"
                  name="accessLevel"
                  value={level}
                  onChange={(e) => setLevel(e.currentTarget.value as AccessLevel)}
                >
                  {LEVELS.filter((l) =>
                    (OFFERED_ACCESS_LEVELS as readonly AccessLevel[]).includes(l.code),
                  ).map((l) => (
                    <option key={l.code} value={l.code}>
                      {accessLevelHe(l.code)}
                    </option>
                  ))}
                </select>
              </label>

            </form>

            {outcome === null ? null : outcome.kind === 'granted' ? (
              <StateBlock
                titleHe={t('access.grantedTitle')}
                bodyHe={`${String(outcome.rows)} ${t('access.rowsWritten')}`}
              />
            ) : outcome.kind === 'revoked' ? (
              <StateBlock
                titleHe={t('access.revokedTitle')}
                // CG-6: one row, one pair. Nobody else lost anything, and the sentence says so.
                bodyHe={t('access.revokedBody')}
              />
            ) : (
              // One classification, the same one every screen uses. The step-up that granting may require
              // is NOT a denial, and `<ActionFailureNotice>` is what keeps the two apart.
              <ActionFailureNotice
          failure={outcome.failure}
          /*
           * THE SCREEN THIS MATTERS MOST ON. `companies.assignment.manage` is declared `per_action`,
           * so granting a colleague access to a company is step-up gated — and with no way to
           * satisfy a step-up, it was unreachable minutes after sign-in. Which means fixing the
           * company register alone would not have let anybody actually grant access to the company
           * they could now see.
           */
          onStepUpSatisfied={() => {
            setOutcome(null);
            grantsRead.reload();
          }}
        />
            )}

            <DataTable<CompanyGrantSummary>
              captionHe={t('access.existing')}
              columns={grantColumns}
              rows={rowsForMember}
              rowKey={(g) => `${g.membershipUid}:${g.companyUid}`}
              filterSummaryHe={
                membershipUid === ''
                  ? `${t('access.showingAll')}: ${String(grants.length)}`
                  : `${t('access.showingForUser')}: ${String(rowsForMember.length)} / ${String(grants.length)}`
              }
              emptyStateHe={t('access.empty')}
            />
          </>
        )}
      </ServerRead>
    </AppShell>
  );
}
